import { SpeciesSurveyDataV1 } from '@omniafishing/core';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveLinear } from '@visx/curve';
import { localPoint } from '@visx/event'; // To get coordinates relative to the SVG
import { GridColumns, GridRows } from '@visx/grid'; // Import Grid components
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { extent } from 'd3-array';
import React, { useCallback, useMemo } from 'react';

// Define margins for the chart area
const margin = { top: 10, right: 10, bottom: 30, left: 30 };

interface ChartCpueProps {
  width: number;
  height: number;
  speciesData: SpeciesSurveyDataV1['species'][keyof SpeciesSurveyDataV1['species']];
}

interface CPUEDataPoint {
  value: number;
  date: Date;
}

type TooltipData = CPUEDataPoint;

const ChartCpue = (props: ChartCpueProps) => {
  const { speciesData, width, height } = props;

  // --- Tooltip Hook ---
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<TooltipData>(); // Specify the data type for the tooltip

  const gearType = speciesData.current_status.primary_gear_type;
  const { values } = speciesData.cpue_series.gear_types[gearType];
  const cpueData: CPUEDataPoint[] = useMemo(
    () =>
      Object.entries(values).map(([year, value]) => ({
        value,
        date: new Date(`${year}-01-02`), // use 1 day offset so timezones don't affect the date
      })),
    [speciesData]
  );

  // Define accessor functions (good practice)
  const getDate = useCallback((d: CPUEDataPoint) => d.date, []);
  const getValue = useCallback((d: CPUEDataPoint) => d.value, []);

  // Calculate inner dimensions based on margins
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const dateDomain = extent(cpueData, getDate) as [Date, Date];

  // --- Calculate Target Number of Ticks for X-Axis ---
  const targetXTicks = useMemo(() => {
    if (!dateDomain || !dateDomain[0] || !dateDomain[1] || cpueData.length < 2) {
      return 1; // Default to 1 tick if data is insufficient
    }
    const firstYear = dateDomain[0].getFullYear();
    const lastYear = dateDomain[1].getFullYear();
    const yearSpan = lastYear - firstYear;

    if (yearSpan <= 0) {
      return 1; // Only one tick if span is 0 or less
    }

    // Aim for roughly one tick per 2 years. Add 1 because numTicks often relates to intervals.
    // Use Math.max(2, ...) to ensure at least start and end ticks if possible.
    const calculatedTicks = Math.max(2, Math.round(yearSpan / 2) + 1);

    // Optional: Limit max ticks to prevent overcrowding on very wide spans / small screens
    // const maxAllowedTicks = Math.floor(xMax / 50); // e.g., max 1 tick every 50px
    // return Math.min(calculatedTicks, maxAllowedTicks);

    return calculatedTicks;
  }, [dateDomain, cpueData.length /*, xMax */]); // Add xMax if using maxAllowedTicks

  // The x scale maps dates to the horizontal space
  const xScale = scaleTime<number>({
    // Domain uses the actual min and max dates from your data
    domain: extent(cpueData, getDate) as [Date, Date], // extent returns [min, max]
    // Range maps to the inner width of the chart
    range: [0, xMax],
  });

  // The y scale maps values to the vertical space
  const yScale = scaleLinear<number>({
    // Domain: Start from 0 (or data min) up to the max value + buffer
    domain: [Math.min(...cpueData.map(getValue)) * 0.9, Math.max(...cpueData.map(getValue)) * 1.1], // Use 1.1 for a 10% buffer, handles empty data case with Math.max(..., 0)
    // Range maps to the inner height (inverted for SVG coordinates)
    range: [yMax, 0],
    nice: true, // Adjusts the domain to nice round values
  });

  // --- Tooltip Event Handlers (memoized with useCallback) ---
  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGCircleElement> | React.MouseEvent<SVGCircleElement>) => {
      // Get the data point associated with the hovered circle
      // We need a way to link the event target back to the data.
      // Using a data attribute on the circle is one way.
      const pointKey = (event.target as SVGCircleElement).getAttribute('data-key');
      const pointData = cpueData.find((d) => getDate(d).getTime().toString() === pointKey);

      if (!pointData) {
        return;
      } // Should not happen if data-key is set correctly

      // Use localPoint to find coordinates relative to the SVG container
      const coords = localPoint(event);
      if (!coords) {
        return;
      }

      showTooltip({
        tooltipData: pointData,
        tooltipLeft: coords.x, // Position tooltip based on mouse/touch event
        tooltipTop: coords.y,
      });
    },
    [showTooltip, cpueData, getDate] // Dependencies for useCallback
  );

  return (
    <div className="relative">
      <svg width={width} height={height}>
        {/* Use a Group to offset the chart content by the margins */}
        <Group left={margin.left} top={margin.top}>
          {/* Render grids first to appear behind other elements */}
          <GridRows
            scale={yScale} // Use the y-scale to determine row positions
            width={xMax} // Extend grid lines across the inner chart width
            stroke={'#000'}
            strokeOpacity={0.1}
            numTicks={4}
          />
          <GridColumns
            scale={xScale} // Use the x-scale to determine column positions
            height={yMax} // Extend grid lines across the inner chart height
            stroke={'#000'}
            strokeOpacity={0.1}
            numTicks={targetXTicks}
          />

          {/* Add Axes */}
          <AxisLeft
            hideAxisLine
            hideTicks
            scale={yScale}
            numTicks={4}
            tickLabelProps={() => ({
              fill: '#333',
              fontSize: 10,
              textAnchor: 'end',
              dy: '0.33em', // Vertical alignment
            })}
          />
          <AxisBottom
            hideAxisLine
            hideTicks
            top={yMax} // Position the bottom axis at the bottom of the inner chart area
            scale={xScale}
            numTicks={targetXTicks}
            tickFormat={(date) => (date instanceof Date ? date.getFullYear().toString() : '')} // Format ticks to show only the year
            tickLabelProps={() => ({
              fill: '#333',
              fontSize: 10,
              textAnchor: 'middle',
            })}
          />

          {/* The Line Path */}
          <LinePath<CPUEDataPoint>
            data={cpueData}
            // Use the accessor functions and scales
            x={(d) => xScale(getDate(d)) ?? 0} // Use ?? 0 as fallback if scale returns undefined
            y={(d) => yScale(getValue(d)) ?? 0}
            stroke={'#000'}
            strokeWidth={2}
            curve={curveLinear}
          />

          {cpueData.map((d, i) => {
            const cx = xScale(getDate(d));
            const cy = yScale(getValue(d));

            // Ensure coordinates are valid numbers before rendering circle
            if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) {
              return null;
            }
            const key = getDate(d).getTime().toString();

            return (
              <circle
                key={`point-${key}`}
                data-key={key}
                cx={cx}
                cy={cy}
                r={5}
                fill={'#fff'}
                stroke="#000"
                strokeWidth={2}
                pointerEvents="all"
                // Show tooltip on hover or touch
                onMouseEnter={handleTooltip}
                onMouseMove={handleTooltip} // Use mouseMove for smoother updates if needed
                onTouchStart={handleTooltip}
                onTouchMove={handleTooltip}
                // Hide tooltip on leave
                onMouseLeave={hideTooltip}
                onTouchEnd={hideTooltip}
                // Add visual feedback on hover (optional)
                style={{ cursor: 'pointer', transition: 'fill 0.2s ease' }}
                onMouseOver={(e) => (e.target as SVGCircleElement).setAttribute('fill', '#000')}
                onMouseOut={(e) => (e.target as SVGCircleElement).setAttribute('fill', '#fff')}
              />
            );
          })}
        </Group>
      </svg>
      <p className="text-[11px] text-[#061622] flex gap-1 items-center">
        <span className="w-3 h-3 rounded-full border-2 border-black" />
        Yearly CPUE
      </p>
      {/* --- Tooltip Rendering --- */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          key={Math.random()} // Needed for bounds detection synchronization
          top={tooltipTop} // Position provided by useTooltip hook
          left={tooltipLeft} // Position provided by useTooltip hook
          style={{
            ...defaultStyles,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none' as const, // Prevent tooltip from blocking mouse events
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            lineHeight: 1.5,
          }}
          // You can adjust offset if needed, e.g., move slightly above pointer
          // offsetTop={-10}
        >
          {/* Content of the tooltip */}
          <div>
            <strong>Year:</strong> {getDate(tooltipData).getFullYear()}
          </div>
          <div>
            <strong>CPUE:</strong> {getValue(tooltipData).toFixed(2)}
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default ChartCpue;
